import { Component, ViewChild } from '@angular/core';
import { IonicPage, NavController, NavParams, Platform } from 'ionic-angular';
import { ActionType, StartAndEnd } from '../../../models/projects/validation.models';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { GapOverlapValidationRow } from '../vis.models';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { process, State } from '@progress/kendo-data-query';
import { columns } from './gap-overlap.config';
import { DailyLogOperationInstance } from '../../../models/projects/operation.models';
import { DailyLogValidationService } from '../../../services/daily-log/daily-log-validation/daily-log-validation.service';
import { OperationDetailsEditPage } from '../../operations/operation-details-edit/operation-details-edit';
import { DateService } from '../../../services/date/date.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { AppConfig } from '../../../app/app.config';

@IonicPage()
@Component({
  selector: 'page-gap-overlap',
  templateUrl: 'gap-overlap.html',
})
export class GapOverlapValidationPage {
  @ViewChild('timeline') timelineDiv: any;
  @ViewChild('contractualOverlapGrid') grid?: GridComponent;

  config = AppConfig.settings;
  isWeb: boolean;
  overlaps: StartAndEnd[] = [];
  rows: GapOverlapValidationRow[] = [];
  instances: DailyLogOperationInstance[] = [];
  loaded: boolean = false;
  dailyLogDate: PtLuxonDate;

  gridData: GridDataResult;
  columns = columns;

  state: State = {
    take: 500,
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      {
        field: 'date',
        dir: 'asc'
      },
      {
        field: 'endDate',
        dir: 'asc'
      },
    ],
  };

  constructor(
    public navParams: NavParams,
    public platform: Platform,
    public operationService: OperationService,
    public dailyLogValidatorService: DailyLogValidationService,
    public navCtrl: NavController,
    public dateService: DateService,
    public dailyLogService: DailyLogService,
    private timeZoneService: TimeZoneService,
    private appDataSyncService: AppDataSyncService,
  ) {
    this.dailyLogDate = navParams.get('dailyLogDate');
  }

  async reloadInstances() {
    const ids = this.instances.map(p => p.id ? p.id : p.localId);

    // If user edits an operation via this page - then when the operation is saved, existing PSOs in the local DB get deleted, so we need to
    // refresh them from the API here or there will only be 1 PSO in the local DB.
    // TODO - This should really be refactored out - there's no need to delete all the local PSOs any more on operation save
    await this.appDataSyncService.updateDailyLogDataForDate({date: this.dailyLogDate.dateTime}, true);
    this.instances = await this.operationService.getProjectSpecificOperationInstances(ids);

    // todo - remove if not needed
    // if (this.instances) {
    //   for (let i = 0; i < this.instances.length; i++) {
    //     this.instances[i] = await this.operationService.removeSystemOffsetsFromInstanceTimes(this.instances[i]);
    //   }
    // }
  }

  async ionViewDidEnter() {
    this.loaded = false;
    await this.reloadInstances();
    await this.loadData();
    this.loaded = true;
  }

  async ionViewDidLoad() {
    this.isWeb = this.platform.is('core');
    this.instances = this.navParams.get('instances');
  }

  async loadData() {
    this.rows = [];
    const gaps = await this.dailyLogValidatorService.findGapsInOperations(this.instances.filter(p =>
      !p.isConcurrentOperation && !p.isInterruption || (p.isConcurrentOperation && (p.switchTimes.some(sw => !sw.isConcurrent)))), this.dailyLogDate);

    gaps.forEach(element => {
      element.type = ActionType.OperationGap;
    });

    const overlaps = await this.dailyLogValidatorService.findOverlapTimes(this.instances, this.dailyLogDate);

    overlaps.forEach(element => {
      element.type = ActionType.OperationOverlap;
    });

    this.overlaps = overlaps.concat(gaps);

    const operationIds = this.overlaps
      .map(p => p.operationInstanceId);

    const previousOperationIds = this.overlaps
      .map(p => p.previousOperatioInstanceId)
      .filter((opId, index, array) => array.indexOf(opId) === index);

    const allIds = operationIds.concat(previousOperationIds)
      .filter(p => p != null);

    const instances = await this.operationService.getProjectSpecificOperationInstances(allIds);

    if (!instances || instances.length < 1) {
      this.gridData = process(this.rows, this.state);
      return;
    }

    for (const overlap of this.overlaps) {
      if (!!overlap.operationInstance) {
        const newRow = new GapOverlapValidationRow();
        newRow.operationName = overlap.operationInstance.projectSpecificOperation.name
          ? overlap.operationInstance.projectSpecificOperation.name
          : overlap.operationInstance.projectSpecificOperation.operationSummary.name;
        newRow.operationClassification = overlap.operationInstance.projectSpecificOperation.operationSummary.classificationName;
        newRow.instanceId = overlap.operationInstanceId;

        if (overlap.type === ActionType.OperationGap) {
          newRow.error = `Gap between ${!overlap.previousOperation
            ? 'start of day'
            : overlap.previousOperation.projectSpecificOperation.name
              ? overlap.previousOperation.projectSpecificOperation.name
              : overlap.previousOperation.projectSpecificOperation.operationSummary.name}
            and ${!overlap.operationInstance
            ? 'end of day'
            : overlap.operationInstance.projectSpecificOperation.name
              ? overlap.operationInstance.projectSpecificOperation.name
              : overlap.operationInstance.projectSpecificOperation.operationSummary.name}`;

          newRow.startTimeShow = await this.dateService.setFullTimeString(overlap.startTime.dateTime);
          newRow.endTimeShow = await this.dateService.setFullTimeString(overlap.endTime.dateTime);
          newRow.date = overlap.startTime.getDate();
          newRow.endDate = overlap.endTime.getDate();
        } else {
          if (!!overlap.previousOperation && !!overlap.operationInstance) {
            newRow.error = `Overlap between ${!overlap.previousOperation
              ? 'start of day'
              : overlap.previousOperation.projectSpecificOperation.name
                ? overlap.previousOperation.projectSpecificOperation.name
                : overlap.previousOperation.projectSpecificOperation.operationSummary.name}
            and ${!overlap.operationInstance
              ? 'end of day'
              : overlap.operationInstance.projectSpecificOperation.name
                ? overlap.operationInstance.projectSpecificOperation.name
                : overlap.operationInstance.projectSpecificOperation.operationSummary.name}`;

            const overlapZone = this.operationService.findOverlapZone(
              overlap.operationInstance.startTime.getDate(),
              overlap.operationInstance.endTime.getDate(),
              overlap.previousOperation.startTime.getDate(),
              overlap.previousOperation.endTime.getDate());

            newRow.startTimeShow = await this.dateService.setFullTimeString(overlapZone.startTime.dateTime);
            newRow.endTimeShow = await this.dateService.setFullTimeString(overlapZone.endTime.dateTime);
            newRow.date = overlapZone.startTime.getDate();
            newRow.endDate = overlapZone.endTime.getDate();
          }
        }

        this.rows.push(newRow);
      }

      if (!!overlap.previousOperation) {
        const newRow = new GapOverlapValidationRow();

        newRow.operationName = overlap.previousOperation.projectSpecificOperation.name
          ? overlap.previousOperation.projectSpecificOperation.name
          : overlap.previousOperation.projectSpecificOperation.operationSummary.name;
        newRow.operationClassification = overlap.previousOperation.projectSpecificOperation.operationSummary.classificationName;
        newRow.instanceId = overlap.previousOperatioInstanceId;

        if (overlap.type === ActionType.OperationGap) {
          newRow.error = `Gap between ${!overlap.previousOperation
            ? 'start of day'
            : overlap.previousOperation.projectSpecificOperation.name
              ? overlap.previousOperation.projectSpecificOperation.name
              : overlap.previousOperation.projectSpecificOperation.operationSummary.name}
            and ${!overlap.operationInstance
            ? 'end of day'
            : overlap.operationInstance.projectSpecificOperation.name
              ? overlap.operationInstance.projectSpecificOperation.name
              : overlap.operationInstance.projectSpecificOperation.operationSummary.name}`;

          newRow.startTimeShow = await this.dateService.setFullTimeString(overlap.startTime.dateTime);
          newRow.endTimeShow = await this.dateService.setFullTimeString(overlap.endTime.dateTime);
          newRow.date = overlap.startTime.getDate();
          newRow.endDate = overlap.endTime.getDate();
        } else {
          if (!!overlap.previousOperation && !!overlap.operationInstance) {
            newRow.error = `Overlap between ${!overlap.previousOperation
              ? 'start of day'
              : overlap.previousOperation.projectSpecificOperation.name
                ? overlap.previousOperation.projectSpecificOperation.name
                : overlap.previousOperation.projectSpecificOperation.operationSummary.name}
            and ${!overlap.operationInstance
              ? 'end of day'
              : overlap.operationInstance.projectSpecificOperation.name
                ? overlap.operationInstance.projectSpecificOperation.name
                : overlap.operationInstance.projectSpecificOperation.operationSummary.name}`;

            const overlapZone = this.operationService.findOverlapZone(
              overlap.operationInstance.startTime.getDate(),
              overlap.operationInstance.endTime.getDate(),
              overlap.previousOperation.startTime.getDate(),
              overlap.previousOperation.endTime.getDate());

            if (!overlapZone) {
              return;
            }

            newRow.startTimeShow = await this.dateService.setFullTimeString(overlapZone.startTime.dateTime);
            newRow.endTimeShow = await this.dateService.setFullTimeString(overlapZone.endTime.dateTime);
            newRow.date = overlapZone.startTime.getDate();
            newRow.endDate = overlap.endTime.getDate();
          }
        }

        this.rows.push(newRow);
      }
    }

    this.rows = this.rows.sort((a, b) => a.date.toMillis() - b.date.toMillis());
    this.gridData = process(this.rows, this.state);
  }

  async edit(id: number) {
    const instance = await this.operationService.getProjectSpecificOperationInstance(id);
    this.navCtrl.push(OperationDetailsEditPage, {instance: instance}, {animation: 'ios-transition'});
  }
}



// WEBPACK FOOTER //
// ./src/pages/validation/gap-overlap/gap-overlap.ts